import React from 'react';
import {connect} from 'react-redux';
import {push} from 'react-router-redux';

import {Container} from 'containers/types';
import Page from 'components/Page';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import Loading from 'components/Loading';
import ListContent from 'components/ListContent';
import {withSorting, SORTING, WithSortingProps} from 'components/withSorting';
import {
  withPagination,
  PAGINATION,
  WithPaginationProps
} from 'components/withPagination';
import {withFilters, FILTERS, WithFiltersProps} from 'components/withFilters';
import {BookingSetTable, BookingSetFilters, SubNavigation} from './components';

import {exportCsvInBackground, selectExporting} from 'ducks/files';
import {composeActions} from 'ducks/common';
import {fetchBookingTypes, selectBookingTypes} from 'ducks/bookings';
import {
  fetchBookingSets,
  selectBookingSets,
  selectBookingSetsAreLoading,
  fetchDepositSummaryStatuses,
  selectDepositSummaryStatuses
} from 'ducks/bookingSets';
import {reverse} from 'utils/urls';
import {BOOKING_DETAIL_URL, BOOKING_SET_DETAIL_URL} from 'config/urls';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';
import {exportBookingSetsCsvInBackground as exportBookingSetsCsvInBackgroundSdk} from 'sdk/bookingSets';

import './styles.css';

const BOOKING_SETS_CSV_FILENAME = 'booking_sets';

const initialFilters = {
  tenant: null,
  search: '',
  startDateAfter: '',
  startDateBefore: '',
  endDateAfter: '',
  endDateBefore: '',
  statuses: [],
  bookingTypes: [],
  depositStatuses: []
};

class BookingSetList extends React.Component<
  Container &
    WithSortingProps &
    LoginRequiredProps &
    WithPaginationProps &
    WithFiltersProps<typeof initialFilters> &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  componentDidMount() {
    const {locationRef} = this.props.match.params;

    // TODO: Add permissions
    this.props.fetchBookingTypes();
    this.props.fetchBookingSets({locationRef});
    this.props.fetchDepositSummaryStatuses();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.match.params.locationRef !== this.props.match.params.locationRef
    ) {
      this.props.fetchBookingSets({
        locationRef: this.props.match.params.locationRef
      });
    }
  }

  onSort = fieldName => {
    const {locationRef} = this.props.match.params;
    const pagination = this.props.getPagination();

    this.props.onSort(fieldName, [
      () =>
        this.props.onPaginationChange({
          pageSize: pagination.pageSize,
          offset: 0
        }),
      () => fetchBookingSets({locationRef})
    ]);
  };

  setFilter = (field: string, value: any) => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    this.props.composeActions([
      () => this.props.setFilter(field, value),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchBookingSets({locationRef})
    ]);
  };

  onPageChange = ({offset, pageSize}) => {
    const {locationRef} = this.props.match.params;

    this.props.onPaginationChange({pageSize, offset}, [
      () => fetchBookingSets({locationRef})
    ]);
  };

  clearFilters = () => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    this.props.composeActions([
      () => this.props.clearFilters(),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchBookingSets({locationRef})
    ]);
  };

  redirectToBookingSetDetail = ({bookingSetId}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        this.props.push(
          reverse(BOOKING_SET_DETAIL_URL, {
            locationRef,
            bookingSetId
          })
        );
      }
    );
  };

  redirectToBookingDetail = ({bookingId}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        this.props.push(
          reverse(BOOKING_DETAIL_URL, {
            locationRef,
            bookingId
          })
        );
      }
    );
  };

  exportBookingSetsCsvInBackground = () => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_CSV_EXPORT_FILE
      },
      () =>
        this.props.exportCsvInBackground({
          exportData: {
            locationRef,
            filters: this.props.getFilters(),
            sort: this.props.getSort()
          },
          exportSdk: exportBookingSetsCsvInBackgroundSdk,
          fileName: BOOKING_SETS_CSV_FILENAME
        })
    );
  };

  render() {
    const {locationRef} = this.props.match.params;
    const {
      bookingSets,
      bookingSetsLoading,
      bookingTypes,
      depositSummaryStatuses
    } = this.props;

    return (
      <Page activeTab="bookingSets" locationRef={locationRef}>
        <ListContent
          className="BookingSetList"
          filters={this.props.getFilters()}
          initialFilters={initialFilters}
          setFilter={this.setFilter}
          navigationExtraElement={SubNavigation}>
          <ListContent.Content>
            {bookingSetsLoading && <Loading />}
            {!bookingSetsLoading && (
              <BookingSetTable
                onPageChange={this.onPageChange}
                pagination={this.props.getPagination()}
                bookingSets={bookingSets}
                sort={this.props.getSort()}
                onSort={this.onSort}
                redirectToBookingSetDetail={this.redirectToBookingSetDetail}
                redirectToBookingDetail={this.redirectToBookingDetail}
                exportBookingSetsCsvInBackground={
                  this.exportBookingSetsCsvInBackground
                }
                exportingBookingSetsCsv={this.props.exportingBookingSetsCsv}
              />
            )}
          </ListContent.Content>
          <ListContent.Filters>
            <BookingSetFilters
              locationRef={locationRef}
              bookingTypes={bookingTypes}
              depositSummaryStatuses={depositSummaryStatuses}
              setFilter={this.setFilter}
              getFilters={this.props.getFilters}
              clearFilters={this.clearFilters}
            />
          </ListContent.Filters>
        </ListContent>
      </Page>
    );
  }
}

const mapStateToProps = state => {
  return {
    bookingSets: selectBookingSets(state),
    bookingSetsLoading: selectBookingSetsAreLoading(state),
    bookingTypes: selectBookingTypes(state),
    depositSummaryStatuses: selectDepositSummaryStatuses(state),
    exportingBookingSetsCsv: selectExporting(state, BOOKING_SETS_CSV_FILENAME)
  };
};

const mapDispatchToProps = {
  fetchBookingSets,
  fetchBookingTypes,
  fetchDepositSummaryStatuses,
  composeActions,
  push,
  requiresPermission,
  exportCsvInBackground
};

const componentWithSorting = withSorting(
  BookingSetList,
  SORTING.BOOKING_SET_LIST,
  {saveToUrl: true, clearSortingOnUnmount: false}
);

const componentWithSortingAndPagination = withPagination(
  componentWithSorting,
  PAGINATION.BOOKING_SET_LIST,
  {
    saveToUrl: true
  }
);

const componentWithSortingPaginationAndFilters = withFilters(
  componentWithSortingAndPagination,
  FILTERS.BOOKING_SET_LIST,
  {initialFilters, saveToUrl: true}
);

export default connect(mapStateToProps, mapDispatchToProps)(
  LoginRequired(componentWithSortingPaginationAndFilters)
);
